import { FC, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Chip, Divider, Paper, Typography } from '@mui/material';
import { Add as AddIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import type { Primal } from '../../types';

// ===== SORTABLE PRIMAL ROW =====

interface SortablePrimalRowProps {
  primal: Primal;
  cutCount: number;
  isActive: boolean;
  onClick: () => void;
}

const SortablePrimalRow: FC<SortablePrimalRowProps> = ({
  primal,
  cutCount,
  isActive,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: primal.id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <Paper
        elevation={isActive ? 2 : 0}
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 1.5,
          mb: 0.5,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: isActive ? 'primary.main' : 'transparent',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            bgcolor: isActive ? 'primary.dark' : 'action.hover',
            '& .primal-drag-handle': { opacity: 1 },
          },
          userSelect: 'none',
          minWidth: 180,
          justifyContent: 'space-between',
        }}
      >
        {/* Drag handle */}
        <Box
          className="primal-drag-handle"
          component="span"
          sx={{
            opacity: 0,
            transition: 'opacity 0.2s',
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: isActive ? 'primary.contrastText' : 'text.secondary',
            flexShrink: 0,
            '&:active': { cursor: 'grabbing' },
          }}
          {...attributes}
          {...listeners}
          aria-label={`Drag ${primal.title} to reorder`}
          onClick={(e) => e.stopPropagation()} // don't activate tab when clicking handle
        >
          <DragIcon fontSize="small" />
        </Box>

        {/* Label */}
        <Typography
          variant="body2"
          fontWeight={isActive ? 600 : 400}
          noWrap
          sx={{ flex: 1, textAlign: 'right' }}
        >
          {primal.title}
        </Typography>

        {/* Cut count badge */}
        <Chip
          label={cutCount}
          size="small"
          sx={{
            ml: 0.5,
            height: 18,
            fontSize: '0.65rem',
            bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'action.selected',
            color: isActive ? 'primary.contrastText' : 'text.secondary',
            flexShrink: 0,
          }}
        />
      </Paper>
    </Box>
  );
};

// ===== STATIC TAB ROW (for Trim, Extras, Specifications) =====

interface StaticTabRowProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  count: number;
}

const StaticTabRow: FC<StaticTabRowProps> = ({
  label,
  isActive,
  onClick,
  count,
}) => (
  <Paper
    elevation={isActive ? 2 : 0}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1,
      py: 1.5,
      mb: 0.5,
      cursor: 'pointer',
      borderRadius: 1,
      bgcolor: isActive ? 'primary.main' : 'transparent',
      color: isActive ? 'primary.contrastText' : 'text.primary',
      '&:hover': {
        bgcolor: isActive ? 'primary.dark' : 'action.hover',
      },
      userSelect: 'none',
      minWidth: 180,
      justifyContent: 'flex-end',
    }}
  >
    <Typography variant="body2" fontWeight={isActive ? 600 : 400} noWrap>
      {label}
    </Typography>
    <Chip
      label={count}
      size="small"
      sx={{
        ml: 0.5,
        height: 18,
        fontSize: '0.65rem',
        bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'action.selected',
        color: isActive ? 'primary.contrastText' : 'text.secondary',
        flexShrink: 0,
      }}
    />
  </Paper>
);

// ===== SORTABLE PRIMAL SIDEBAR =====

export interface SortablePrimalSidebarProps {
  primals: Primal[];
  /** Map of groupId → cut count */
  cutCounts: Record<string, number>;
  trimCount: number;
  extrasCount: number;
  specificationsCount: number;
  activeTab: number;
  onTabChange: (index: number) => void;
  onReorder: (orderedIds: string[]) => Promise<void>;
  /** Called when the user clicks "Add Primal" */
  onAddPrimal?: () => void;
}

/**
 * Sortable vertical sidebar that replaces MUI Tabs for the primal list.
 * Primals can be dragged to reorder them (persisted via `onReorder`).
 * Trim, Extras, and Specifications appear as static non-draggable entries
 * at the bottom, matching the original tab layout.
 * @param root0 - The component props.
 * @param root0.primals - The list of primals to display.
 * @param root0.cutCounts - Map of groupId to cut count.
 * @param root0.trimCount - Number of trim items.
 * @param root0.extrasCount - Number of extras items.
 * @param root0.specificationsCount - Number of specification items.
 * @param root0.activeTab - Currently active tab index.
 * @param root0.onTabChange - Callback when a tab is selected.
 * @param root0.onReorder - Called with ordered primal IDs after drag.
 * @param root0.onAddPrimal - Called when the user clicks "Add Primal".
 * @returns The sortable sidebar element.
 * @example
 * <SortablePrimalSidebar
 *   primals={catalog.primals}
 *   cutCounts={cutCountsMap}
 *   trimCount={catalog.trim.length}
 *   extrasCount={catalog.extras.length}
 *   specificationsCount={catalog.specifications.length}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   onReorder={reorderPrimals}
 * />
 */
export const SortablePrimalSidebar: FC<SortablePrimalSidebarProps> = ({
  primals,
  cutCounts,
  trimCount,
  specificationsCount,
  activeTab,
  onTabChange,
  onReorder,
  onAddPrimal,
}) => {
  const [orderedIds, setOrderedIds] = useState<string[]>(() =>
    primals.map((p) => p.id),
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sync when primals list changes (add/remove)
  const serverIds = primals.map((p) => p.id);
  const hasNewItems = serverIds.some((id) => !orderedIds.includes(id));
  const hasRemovedItems = orderedIds.some((id) => !serverIds.includes(id));
  if (hasNewItems || hasRemovedItems) {
    setOrderedIds(serverIds);
  }

  const sortedPrimals = orderedIds
    .map((id) => primals.find((p) => p.id === id))
    .filter(Boolean) as Primal[];

  const activeItem = activeId ? primals.find((p) => p.id === activeId) : null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = orderedIds.indexOf(active.id as string);
    const newIndex = orderedIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedIds, oldIndex, newIndex);

    // If the active tab was the dragged item, keep it selected at its new index
    if (activeTab === oldIndex) {
      onTabChange(newIndex);
    } else if (activeTab > oldIndex && activeTab <= newIndex) {
      onTabChange(activeTab - 1);
    } else if (activeTab < oldIndex && activeTab >= newIndex) {
      onTabChange(activeTab + 1);
    }

    setOrderedIds(newOrder);
    await onReorder(newOrder);
  };

  const primalCount = primals.length;

  return (
    <Box
      sx={{
        borderRight: 1,
        borderColor: 'divider',
        minWidth: 200,
        pr: 1,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedPrimals.map((primal, index) => (
            <SortablePrimalRow
              key={primal.id}
              primal={primal}
              cutCount={cutCounts[primal.id] ?? 0}
              isActive={activeTab === index}
              onClick={() => onTabChange(index)}
            />
          ))}
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <Paper
              elevation={4}
              sx={{
                px: 1.5,
                py: 1.5,
                minWidth: 180,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 1,
                cursor: 'grabbing',
              }}
            >
              <Typography variant="body2" fontWeight={600} noWrap>
                {activeItem.title}
              </Typography>
            </Paper>
          )}
        </DragOverlay>
      </DndContext>

      {/* Add Primal button — sits just above the Trim/Extras/Specs divider */}
      {onAddPrimal && (
        <Button
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddPrimal}
          variant="outlined"
          sx={{ my: 1, justifyContent: 'center' }}
        >
          Add Group
        </Button>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Static tabs for Trim, Extras, Specifications */}
      <StaticTabRow
        label="Trim"
        count={trimCount}
        isActive={activeTab === primalCount}
        onClick={() => onTabChange(primalCount)}
      />

      <StaticTabRow
        label="Specifications"
        count={specificationsCount}
        isActive={activeTab === primalCount + 2}
        onClick={() => onTabChange(primalCount + 2)}
      />
    </Box>
  );
};
